import { useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import NoData from "components/NoData/NoData.view";
import { useStyles } from "./DowntimeSummaryByProductionLine.styles";
import { GroupDataByProductionLine } from "./DowntimeSummaryByProductionLineInterfaces";
import { UptimeDowntimeBar } from "./UptimeDowntimeBar/UptimeDowntimeBar.view";
import { truncateLongText } from "utilities/utilities";
import { TrendReportContext } from "contexts/TrendReportContext/TrendReportContext";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";

const DowntimeSummaryByProductionLine = () => {
    const classes = useStyles();
    const {
        showAllProductionLines,
        handleSelectedProductionLineChange,
        handleSelectedPreviousProductionLineChange,
        selectedProductionLine,
    } = useContext(TrendReportContext);
    const { millId } = useParams();

    const { reportRangeStartDate: startDate, reportRangeEndDate: endDate } = useContext(MillDashboardContext);

    const queryFilters = useMemo(
        () => ({
            startDate,
            endDate,
        }),
        [endDate, startDate],
    );

    const { productionLineSummary } = MillsEntity.GetUptimesVsDowntimesTotalsPerMachineCenterMillDashboard(
        queryFilters,
        Number(millId),
    );

    const groupedDataByProductionLine: GroupDataByProductionLine = useMemo(
        () =>
            productionLineSummary.reduce((groups: GroupDataByProductionLine, obj: any) => {
                const productionLineId = obj.production_line_id;
                const productionLineName = obj.production_line.code || obj.production_line.name;

                if (!groups[productionLineId]) {
                    groups[productionLineId] = {
                        productionLineId: productionLineId,
                        productionLineName: productionLineName,
                        downtime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                        uptime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                    };
                }

                groups[productionLineId].downtime.seconds += obj.downtime_seconds;
                groups[productionLineId].downtime.occurrences += obj.downtime_occurrences;
                groups[productionLineId].uptime.seconds += obj.uptime_seconds;
                groups[productionLineId].uptime.occurrences += obj.uptime_occurrences;

                return groups;
            }, {}),
        [productionLineSummary],
    );

    return Object.values(groupedDataByProductionLine).length > 0 ? (
        <Box className={classes.mainWrapper}>
            <section className={classes.leftSection}>
                {Object.values(groupedDataByProductionLine).length >= 1 && (
                    <div>
                        {Object.values(groupedDataByProductionLine).map((machineCenter, index) => {
                            return (
                                <UptimeDowntimeBar
                                    key={`${index + Math.random()}`}
                                    onClick={() => {
                                        handleSelectedProductionLineChange({
                                            id: machineCenter.productionLineId,
                                            name: machineCenter.productionLineName,
                                        });
                                        handleSelectedPreviousProductionLineChange({
                                            id: machineCenter.productionLineId,
                                            name: machineCenter.productionLineName,
                                        });
                                    }}
                                    selected={selectedProductionLine?.id === machineCenter.productionLineId}
                                    allProductionLinesSelected={showAllProductionLines}
                                    productionLineName={truncateLongText(machineCenter.productionLineName, 12)}
                                    uptimeValue={machineCenter.uptime.seconds / 60}
                                    downtimeValue={machineCenter.downtime.seconds / 60}
                                />
                            );
                        })}
                    </div>
                )}
            </section>
        </Box>
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeSummaryByProductionLine;
